import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ParticipationModal = ({ isOpen, onClose, giveaway, onSubmit }) => {
  const [formData, setFormData] = useState({
    giveawayCode: '',
    agreedToTerms: false,
    subscribeToUpdates: false
  });
  const [detectedPlatforms, setDetectedPlatforms] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Social media platforms configuration with follow links
  const socialPlatforms = [
    { 
      key: 'youtube', 
      name: 'YouTube', 
      icon: 'Youtube', 
      color: 'text-red-600', 
      bgColor: 'bg-red-50',
      prefix: 'YT',
      followLink: 'https://youtube.com/@yourbrand'
    },
    { 
      key: 'facebook', 
      name: 'Facebook', 
      icon: 'Facebook', 
      color: 'text-blue-600', 
      bgColor: 'bg-blue-50',
      prefix: 'FB',
      followLink: 'https://facebook.com/yourbrand'
    },
    { 
      key: 'instagram', 
      name: 'Instagram', 
      icon: 'Instagram', 
      color: 'text-pink-600', 
      bgColor: 'bg-pink-50',
      prefix: 'IG',
      followLink: 'https://instagram.com/yourbrand'
    },
    { 
      key: 'tiktok', 
      name: 'TikTok', 
      icon: 'Music', 
      color: 'text-black', 
      bgColor: 'bg-gray-50',
      prefix: 'TT',
      followLink: 'https://tiktok.com/@yourbrand'
    },
    { 
      key: 'x', 
      name: 'X (Twitter)', 
      icon: 'Twitter', 
      color: 'text-black', 
      bgColor: 'bg-gray-50',
      prefix: 'X',
      followLink: 'https://x.com/yourbrand'
    }
  ];

  // Detect platform from code
  const detectPlatformFromCode = (code) => {
    if (!code) return null;
    
    const upperCode = code.toUpperCase();
    for (const platform of socialPlatforms) {
      if (upperCode.startsWith(platform.prefix)) {
        return platform;
      }
    }
    return null;
  };

  // Update detected platforms when code changes
  useEffect(() => {
    const platform = detectPlatformFromCode(formData.giveawayCode);
    if (platform) {
      setDetectedPlatforms([platform]);
    } else {
      setDetectedPlatforms([]);
    }
  }, [formData.giveawayCode]);

  // Handle social media icon click
  const handleSocialIconClick = (platform) => {
    window.open(platform.followLink, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen || !giveaway) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.giveawayCode.trim()) {
      newErrors.giveawayCode = 'Giveaway code is required';
    }

    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      onSubmit({
        giveawayId: giveaway.id,
        ...formData,
        detectedPlatform: detectedPlatforms[0]?.key || null,
        entryDate: new Date().toISOString()
      });
      
      // Reset form
      setFormData({
        giveawayCode: '',
        agreedToTerms: false,
        subscribeToUpdates: false
      });
      
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEntryMethodDescription = () => {
    switch (giveaway.entryMethod) {
      case 'code':
        return 'Enter your giveaway code to participate';
      case 'social':
        return 'Share this giveaway on your social media to enter';
      default:
        return 'Follow the requirements to enter this giveaway';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative p-6 border-b border-accent-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-surface rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-accent-600" />
          </button>
          
          <div className="pr-8">
            <h2 className="font-heading font-bold text-xl text-accent-800 mb-2">
              Enter Giveaway
            </h2>
            <p className="text-accent-600 text-sm">
              {getEntryMethodDescription()}
            </p>
          </div>
        </div>

        {/* Giveaway Info */}
        <div className="p-6 border-b border-accent-100">
          <div className="flex gap-4">
            <img
              src={giveaway.coverImage}
              alt={giveaway.title}
              className="w-16 h-16 rounded-xl object-cover"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-accent-800 mb-1">
                {giveaway.title}
              </h3>
              <div className="flex items-center gap-2 text-sm text-accent-600 mb-2">
                <img
                  src={giveaway.sponsorLogo}
                  alt={giveaway.sponsor}
                  className="w-4 h-4 rounded-full"
                />
                <span>{giveaway.sponsor}</span>
              </div>
              <div className="text-lg font-bold text-primary">
                {giveaway.prizeValue}
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Giveaway Code Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Enter your giveaway code *
            </label>
            <Input
              type="text"
              placeholder="Enter your giveaway code (e.g., YT1234, FB5678, IG9012)"
              value={formData.giveawayCode}
              onChange={(e) => handleInputChange('giveawayCode', e.target.value)}
              className={errors.giveawayCode ? 'border-error' : ''}
            />
            {errors.giveawayCode && (
              <p className="text-error text-sm mt-1">{errors.giveawayCode}</p>
            )}
          </div>

          {/* Social Media Platforms Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-accent-700">
                Social Media Platforms
              </label>
              {detectedPlatforms.length > 0 && (
                <div className="flex items-center gap-2">
                  <Icon name="Check" size={16} className="text-green-500" />
                  <span className="text-sm text-green-600 font-medium">
                    {detectedPlatforms[0].name} detected
                  </span>
                </div>
              )}
            </div>
            
            <p className="text-sm text-accent-600 mb-4">
              Follow us on different social media platforms to maximize your chances! Click on any platform to follow us.
            </p>
            
            {/* Social Platform Indicators */}
            <div className="grid grid-cols-5 gap-2 mb-4">
              {socialPlatforms.map((platform) => {
                const isDetected = detectedPlatforms.some(p => p.key === platform.key);
                
                return (
                  <div
                    key={platform.key}
                    onClick={() => handleSocialIconClick(platform)}
                    className={`relative p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${
                      isDetected
                        ? `${platform.bgColor} border-green-200 ring-2 ring-green-100`
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Icon 
                        name={platform.icon} 
                        size={20} 
                        className={isDetected ? platform.color : 'text-gray-400'}
                      />
                      <span className={`text-xs text-center font-medium ${
                        isDetected ? 'text-accent-800' : 'text-gray-500'
                      }`}>
                        {platform.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {platform.prefix}****
                      </span>
                    </div>
                    {isDetected && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <Icon name="Check" size={10} className="text-white" />
                      </div>
                    )}
                    {/* Click indicator */}
                    <div className="absolute inset-0 rounded-lg border border-transparent hover:border-primary/20 transition-colors">
                      <div className="absolute bottom-1 right-1">
                        <Icon name="ExternalLink" size={10} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Platform codes hint */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-medium mb-1">
                    Code Format Examples:
                  </p>
                  <div className="text-xs text-blue-700 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono bg-blue-100 px-1 rounded">YT1234</span>
                      <span>- YouTube codes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono bg-blue-100 px-1 rounded">FB5678</span>
                      <span>- Facebook codes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono bg-blue-100 px-1 rounded">IG9012</span>
                      <span>- Instagram codes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Entry Requirements */}
          <div className="mb-6">
            <h4 className="font-medium text-accent-800 mb-3">Entry Requirements</h4>
            <div className="space-y-2">
              {giveaway.requirements?.map((req, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Icon name="Check" size={16} className="text-success" />
                  <span className="text-sm text-accent-600">{req}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Eligibility */}
          <div className="mb-6 p-4 bg-surface rounded-lg">
            <h4 className="font-medium text-accent-800 mb-2">Eligibility</h4>
            <p className="text-sm text-accent-600">{giveaway.eligibility}</p>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 mb-6">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={formData.agreedToTerms}
                onChange={(e) => handleInputChange('agreedToTerms', e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-accent-700">
                I agree to the{' '}
                <button type="button" className="text-primary hover:underline">
                  Terms and Conditions
                </button>
                {' '}and{' '}
                <button type="button" className="text-primary hover:underline">
                  Privacy Policy
                </button>
              </span>
            </label>
            
            {errors.agreedToTerms && (
              <p className="text-error text-sm">{errors.agreedToTerms}</p>
            )}

            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={formData.subscribeToUpdates}
                onChange={(e) => handleInputChange('subscribeToUpdates', e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-accent-700">
                Subscribe to updates about this giveaway and similar opportunities
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={isSubmitting}
            loading={isSubmitting}
            iconName="Gift"
            iconPosition="left"
            className="shadow-warm"
          >
            {isSubmitting ? 'Submitting Entry...' : 'Submit Entry'}
          </Button>
        </form>

        {/* Footer */}
        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-accent-500">
            You'll receive a confirmation email once your entry is processed
          </p>
        </div>
      </div>
    </div>
  );
};

export default ParticipationModal;